import { useQuery } from '@tanstack/react-query';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  Boxes,
  Building2,
  CheckCircle2,
  ClipboardList,
  FileSearch,
  LayoutDashboard,
  LogOut,
  Mail,
  Package,
  PackageOpen,
  PoundSterling,
  Receipt,
  Settings,
  ShoppingBag,
  ShoppingCart,
  Truck,
  Undo2,
  Users,
} from 'lucide-react';
import { useAuth } from '../auth/useAuth';
import { hasRole, primaryOrgId, ROLE_LABELS } from '../auth/permissions';
import { getCustomer } from '../api/customers';
import { useLogoUrl } from '../hooks/useLogoUrl';
import { BrandMark } from '../components/BrandMark';
import { NotificationBell } from '../components/NotificationBell';

function NavItem({ to, icon: Icon, label }: { to: string; icon: typeof LayoutDashboard; label: string }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
          isActive ? 'bg-brand-50 text-brand-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
        }`
      }
    >
      <Icon className="h-4 w-4" />
      {label}
    </NavLink>
  );
}

// Co-branding strip: Oakford Technology is the platform's own brand,
// always shown; this renders alongside it for anyone logging into a
// customer organisation's part of the portal (Customer Admin, Signing
// Authority or End User), per Perry's brief.
function CoBrandStrip({ orgId }: { orgId: string }) {
  const { data: org } = useQuery({ queryKey: ['customer', orgId], queryFn: () => getCustomer(orgId) });
  const logoUrl = useLogoUrl(orgId, org?.hasLogo);

  if (!org) return null;

  return (
    <div className="mt-3 flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50 px-2.5 py-2">
      {logoUrl ? (
        <img src={logoUrl} alt={`${org.name} logo`} className="h-6 w-6 shrink-0 rounded object-contain" />
      ) : (
        <div
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-[10px] font-bold text-white"
          style={{ backgroundColor: org.brandColor ?? '#64748b' }}
        >
          {org.name.slice(0, 1).toUpperCase()}
        </div>
      )}
      <div className="min-w-0">
        <p className="truncate text-xs font-semibold text-slate-700">{org.name}</p>
        <p className="text-[10px] uppercase tracking-wide text-slate-400">Your organisation</p>
      </div>
    </div>
  );
}

export function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isMspAdmin = user?.isMspAdmin ?? false;
  const coBrandOrgId = primaryOrgId(user);

  const displayRole = isMspAdmin
    ? ROLE_LABELS.MSP_ADMIN
    : user
      ? [...new Set([...user.orgRoles.map((r) => r.role), ...user.siteRoles.map((r) => r.role)])]
          .map((r) => ROLE_LABELS[r])
          .join(' / ') || 'User'
      : '';

  return (
    <div className="flex h-screen bg-slate-50">
      <aside className="flex w-64 shrink-0 flex-col border-r border-slate-200 bg-white">
        <div className="px-5 py-5">
          <div className="flex flex-col items-center gap-2 text-center">
            <BrandMark size="xl" />
            <p className="truncate text-lg font-bold leading-tight tracking-tight text-slate-900">ATLAS PORTAL</p>
          </div>

          {coBrandOrgId && <CoBrandStrip orgId={coBrandOrgId} />}
        </div>

        <nav className="flex-1 space-y-1 px-3">
          <NavItem to="/" icon={LayoutDashboard} label="Dashboard" />
          {isMspAdmin && <NavItem to="/customers" icon={Building2} label="Customers" />}
          {!isMspAdmin && coBrandOrgId && hasRole(user, 'CUSTOMER_ADMIN', 'REPORTS_VIEWER') && (
            <NavItem to={`/customers/${coBrandOrgId}`} icon={Building2} label="My Organisation" />
          )}
          {!isMspAdmin && coBrandOrgId && <NavItem to="/shop" icon={ShoppingCart} label="Shop" />}
          <NavItem to="/orders" icon={ClipboardList} label="Orders" />
          {(isMspAdmin || hasRole(user, 'SIGNING_AUTHORITY')) && (
            <NavItem to="/approvals" icon={CheckCircle2} label="Approvals" />
          )}
          <NavItem to="/loans" icon={PackageOpen} label="Loans" />
          {isMspAdmin && <NavItem to="/catalog" icon={Package} label="Master Catalog" />}
          {isMspAdmin && <NavItem to="/procurement" icon={ShoppingBag} label="Procurement" />}
          {isMspAdmin && <NavItem to="/delivery" icon={Truck} label="Delivery" />}
          {(isMspAdmin || hasRole(user, 'CUSTOMER_ADMIN')) && (
            <NavItem to="/assets" icon={Boxes} label="Asset Register" />
          )}
          {(isMspAdmin || hasRole(user, 'CUSTOMER_ADMIN')) && (
            <NavItem to="/invoices" icon={Receipt} label="Invoices" />
          )}
          {(isMspAdmin || hasRole(user, 'CUSTOMER_ADMIN')) && (
            <NavItem to="/credit-notes" icon={Undo2} label="Credit Notes" />
          )}
          {isMspAdmin && <NavItem to="/profit-loss" icon={PoundSterling} label="Profit & Loss" />}
          {isMspAdmin && <NavItem to="/msp-users" icon={Users} label="MSP Users" />}
          {isMspAdmin && <NavItem to="/audit-log" icon={FileSearch} label="Audit Log" />}
          {isMspAdmin && <NavItem to="/settings" icon={Settings} label="Settings" />}
        </nav>

        <div className="border-t border-slate-100 px-3 py-3">
          <div className="flex items-center gap-3 rounded-lg px-2 py-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-slate-600">
              <Users className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-slate-800">{user?.email}</p>
              <p className="truncate text-xs text-slate-500">{displayRole}</p>
            </div>
          </div>
        </div>
      </aside>

      <main className="flex flex-1 flex-col overflow-hidden">
        <div className="flex h-14 shrink-0 items-center justify-end gap-2 border-b border-slate-200 bg-white px-6">
          <button
            onClick={() => navigate('/notification-preferences')}
            title="Email preferences"
            className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
          >
            <Mail className="h-4 w-4" />
          </button>
          <NotificationBell />
          <button
            onClick={logout}
            title="Log out"
            className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-6xl px-6 py-8">
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
}
